"use client"

import { motion } from "framer-motion"
import { 
  Calculator,
  FileText,
  Shield,
  Users,
  Award,
  Clock,
  CheckCircle,
  Phone,
  Mail,
  MapPin,
  Target,
  Heart,
  Zap,
  TrendingUp,
  BookOpen,
  Headphones,
  Globe,
  ArrowRight
} from "lucide-react"
import Navbar from "../components/Landing/Navbar"

export default function AboutPage() {
  // Data arrays remain the same as your original
 const services = [
    {
      icon: FileText,
      title: "ITR Filing",
      description: "Complete Income Tax Return filing service with expert guidance and maximum refund optimization.",
      features: ["All ITR Forms", "Expert Review", "Maximum Refunds", "E-filing Support"],
    },
    {
      icon: Calculator,
      title: "TDS Services",
      description: "Comprehensive TDS management including calculations, payments, and compliance reporting.",
      features: ["TDS Calculation", "Online Payment", "Quarterly Returns", "Compliance Check"],
    },
    {
      icon: Shield,
      title: "Tax Planning",
      description: "Strategic tax planning to minimize your tax liability and maximize your savings legally.",
      features: ["Investment Planning", "Deduction Optimization", "Regime Comparison", "Future Planning"],
    },
    {
      icon: BookOpen,
      title: "GST Services",
      description: "Complete GST solutions for businesses including registration, filing, and compliance.",
      features: ["GST Registration", "Monthly Returns", "Input Credit", "Compliance Support"],
    },
    {
      icon: Users,
      title: "Business Advisory",
      description: "Expert business advisory services for startups and established businesses.",
      features: ["Business Setup", "Compliance Advisory", "Financial Planning", "Growth Strategy"],
    },
    {
      icon: Headphones,
      title: "Expert Support",
      description: "24/7 expert support from qualified chartered accountants and tax professionals.",
      features: ["Live Chat", "Phone Support", "Video Consultation", "Email Support"],
    },
  ]

  const stats = [
    { number: "50,000+", label: "Happy Clients", icon: Users },
    { number: "₹500Cr+", label: "Tax Savings", icon: TrendingUp },
    { number: "99.9%", label: "Accuracy Rate", icon: CheckCircle },
    { number: "24/7", label: "Expert Support", icon: Clock },
  ]

  const team = [
    {
      name: "Rajesh Kumar",
      role: "Founder & CEO",
      qualification: "CA, CPA",
      experience: "15+ Years",
      image: "/placeholder.svg?height=300&width=300",
    },
    {
      name: "Priya Sharma",
      role: "Head of Tax Advisory",
      qualification: "CA, LLB",
      experience: "12+ Years",
      image: "/placeholder.svg?height=300&width=300",
    },
    {
      name: "Amit Patel",
      role: "Technology Head",
      qualification: "B.Tech, MBA",
      experience: "10+ Years",
      image: "/placeholder.svg?height=300&width=300",
    },
    {
      name: "Sneha Gupta",
      role: "Customer Success Manager",
      qualification: "MBA, CS",
      experience: "8+ Years",
      image: "/placeholder.svg?height=300&width=300",
    },
  ]

  const values = [
    {
      icon: Target,
      title: "Accuracy",
      description: "We ensure 100% accuracy in all tax calculations and filings with multiple quality checks.",
    },
    {
      icon: Heart,
      title: "Trust",
      description: "Building long-term relationships with our clients through transparency and reliability.",
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "Leveraging cutting-edge technology to simplify complex tax processes for everyone.",
    },
    {
      icon: Globe,
      title: "Accessibility",
      description: "Making professional tax services accessible to individuals and businesses across India.",
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-cyan-100">
    <Navbar/>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-cyan-800 mb-4"
          >
            About TaxEase
          </motion.h1>
          <p className="text-lg text-cyan-700 mb-6 max-w-2xl mx-auto">
            India's most trusted digital tax platform, simplifying tax compliance with technology and expert guidance.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-100 text-cyan-800 text-xs">
              <Award className="h-3 w-3 mr-1" /> ISO Certified
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-100 text-cyan-800 text-xs">
              <Shield className="h-3 w-3 mr-1" /> Secure Platform
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-100 text-cyan-800 text-xs">
              <CheckCircle className="h-3 w-3 mr-1" /> 99.9% Accuracy
            </span>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center p-4 bg-white rounded-lg shadow-sm"
            >
              <div className="mx-auto mb-3 w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                <stat.icon className="h-5 w-5 text-cyan-600" />
              </div>
              <div className="text-2xl font-bold text-cyan-700">{stat.number}</div>
              <div className="text-sm text-cyan-600">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -20 }} 
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <h2 className="text-3xl font-bold text-cyan-800">Our Story</h2>
            <p className="text-cyan-700">
              Founded in 2018, TaxEase was born from a vision to make tax compliance accessible, 
              accurate, and stress-free for every Indian.
            </p>
            <p className="text-cyan-700">
              What started as a small team has grown into India's leading digital tax platform, 
              serving thousands of satisfied clients across the country.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-cyan-600 text-white p-8 rounded-xl"
          >
            <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
            <p className="text-cyan-100">
              To democratize tax compliance in India by providing world-class tax services that are 
              accessible, affordable, and accurate for everyone.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-cyan-800 mb-2">Our Services</h2>
            <p className="text-cyan-700">Comprehensive tax services for all your needs</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                    <service.icon className="h-5 w-5 text-cyan-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-cyan-800 mb-2">{service.title}</h3>
                  <p className="text-cyan-700 mb-4 text-sm">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start text-sm text-cyan-700">
                        <CheckCircle className="h-4 w-4 text-cyan-500 mr-2 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-cyan-800 mb-2">Our Values</h2>
            <p className="text-cyan-700">The principles that guide everything we do</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center bg-white p-6 rounded-xl shadow-sm"
              >
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-5 w-5 text-cyan-600" />
                </div>
                <h3 className="text-lg font-semibold text-cyan-800 mb-2">{value.title}</h3>
                <p className="text-sm text-cyan-700">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

     

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-cyan-600 rounded-xl p-8 text-center text-white"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Simplify Your Taxes?</h2>
            <p className="text-cyan-100 mb-6 max-w-xl mx-auto">
              Join thousands who trust TaxEase for their tax compliance needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button className="px-6 py-3 bg-white text-cyan-600 rounded-lg font-medium flex items-center justify-center">
                <Calculator className="h-4 w-4 mr-2" /> Start Filing
              </button>
              <button className="px-6 py-3 border border-white text-white rounded-lg font-medium flex items-center justify-center">
                <Phone className="h-4 w-4 mr-2" /> Talk to Expert
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-12 bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Phone className="h-5 w-5 text-cyan-600" />
            </div>
            <h3 className="font-medium text-cyan-800 mb-1">Call Us</h3>
            <p className="text-cyan-700">+91 9958229051</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Mail className="h-5 w-5 text-cyan-600" />
            </div>
            <h3 className="font-medium text-cyan-800 mb-1">Email Us</h3>
            <p className="text-cyan-700">support@taxease.com</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <MapPin className="h-5 w-5 text-cyan-600" />
            </div>
            <h3 className="font-medium text-cyan-800 mb-1">Visit Us</h3>
            <p className="text-cyan-700">Mumbai, Delhi, Bangalore</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white/90 border-t border-cyan-200 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="p-2 bg-cyan-600 rounded-lg">
              <Calculator className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-cyan-800">TaxEase</span>
          </div>
          <p className="text-cyan-700 mb-4 text-sm">
            Making tax compliance simple, accurate, and accessible for everyone.
          </p>
          <p className="text-xs text-cyan-600">
            © 2024 TaxEase. All rights reserved. | Licensed by CBDT
          </p>
        </div>
      </footer>
    </div>
  )
}